<template>
  <AdminLayout>
    <template #topbar-search>
      <div class="flex flex-column ">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText v-model="searchValue" placeholder="Search by keywords..." size="small" />
        </IconField>
      </div>
    </template>
    <template #header>
      <Breadcrumb :items="items" />
      <Button label="Export" class="accent-button" @click="ExportInstitute" />
    </template>
    <div v-if="currentView !== 'List'" class="back-section">
      <Button icon="pi pi-arrow-left" label="Back" class="p-button-text Caption back-button mb-2" @click="goBack" />
    </div>
    <div class="container">
      <InstitutionList v-if="currentView === 'List'" :tableData="tableData" :isLoading="isLoading"
        :first="(currentPage - 1) * rowsPerPage" :rowsPerPage="rowsPerPage" :totalRecords="totalRecords"
        :currentPage="currentPage" @page-change="onPageChange" @view-profile="viewProfile"
        @status-change="onStatusChange" @delete-student="showDeleteConfirmationDialog" />
      <InstitutePublicProfile v-if="currentView === 'View profile'" :profileDetails="profileDetails"
        :enrolledTutors="enrolledTutors" />
      <DeleteConfirmationDialog v-model:visible="showDeleteConfirmation" :selectedCount="selectedTutors.length"
        :loading="deleteLoading" @confirm="handleDeleteConfirm" />
      <Toast />
    </div>
  </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import InstitutionList from "@/components/dashboard/admin/institutes/institutionList.vue";
import InstitutePublicProfile from "@/components/dashboard/admin/institutes/institutePublicProfile.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue"
import Button from 'primevue/button';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import DeleteConfirmationDialog from '@/components/dashboard/admin/confirmationDialog.vue';
import * as api from "@/api/admin";
import { notifySuccess } from '@/utils/notification';


const currentView = ref("List");
const profileDetails = ref(null);
const enrolledTutors = ref(null);
const tableData = ref([]);
const isLoading = ref(false);
const totalRecords = ref(0);
const rowsPerPage = ref(10);
const currentPage = ref(1);
const searchValue = ref("");
const showDeleteConfirmation = ref(false);
const deleteLoading = ref(false);
const selectedTutors = ref([]);
const toast = useToast();


const fetchInstitutes = async () => {
  try {
    isLoading.value = true;
    const response = await api.getInstitute({
      page: currentPage.value,
      limit: rowsPerPage.value,
      searchTerm: searchValue.value
    });
    tableData.value = response.data.data.institutes.map(institute => ({
      ...institute,
      isActive: institute.isActive ?? true,
    }));
    totalRecords.value = response.data.data.pagination.totalPages;

  } catch (error) {
    console.error("Failed to fetch students:", error);
  } finally {
    isLoading.value = false;
  }
};
const items = computed(() => {
  const breadcrumbItems = [{ label: "Institutions" }];

  if (currentView.value === "View profile") {
    breadcrumbItems.push({ label: profileDetails.value.username });
  }

  return breadcrumbItems;
});

watch(searchValue, () => {
  currentPage.value = 1;
  fetchInstitutes();
});

onMounted(fetchInstitutes);

const onStatusChange = async (rowData) => {
  try {
    await api.updateInstituteStatus(rowData._id);
    fetchInstitutes();
  } catch (err) {
    console.error('Failed to update status', err);
  }
};

const viewProfile = async (rowData) => {
  try {
    const [profile, tutors] = await Promise.allSettled([
      api.getInstituteProfile(rowData._id),
      api.getInstituteEnrolledTutors(rowData._id),
    ]);
    profileDetails.value = profile.value.data.data ?? {};

    enrolledTutors.value = tutors.value.data.data.tutors ?? [];

  } catch (err) {
    console.error("Unexpected error:", err);
  }
  currentView.value = "View profile";
};

const ExportInstitute = async () => {
  try {
    const res = await api.exportInstitute({searchTerm: searchValue.value});
    const filePath = res.data.data.downloadLink;
    const link = document.createElement("a");
    link.href = filePath;
    link.download = "tutors.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notifySuccess("Success!", "The file has been downloaded.");
  } catch (err) {
    console.error("Failed to export", err);
  }
}
const showDeleteConfirmationDialog = (tutorIds) => {
  selectedTutors.value = tutorIds;
  showDeleteConfirmation.value = true;
};

const handleDeleteConfirm = async () => {
  deleteLoading.value = true;
  try {
    await api.deleteInstitute({ tutorIds: selectedTutors.value });
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Successfully deleted ${selectedTutors.value.length} Institue(s)`,
      life: 3000
    });
    await fetchInstitutes();
    showDeleteConfirmation.value = false;
    selectedTutors.value = [];
  } catch (err) {
    console.error("Failed to delete Institue", err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete Institue(s)',
      life: 3000
    });
  } finally {
    deleteLoading.value = false;
  }
};
const onPageChange = ({ page, limit }) => {
  currentPage.value = page;
  rowsPerPage.value = limit;
  fetchInstitutes();
};
const showList = () => {
  currentView.value = 'List';
};

const goBack = () => {
  showList();
};
</script>

<style>
.container {
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.back-section {
  display: flex;
  align-items: center;
}

.back-button {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}

.back-button:hover {
  color: var(--gray-600) !important;
  background-color: var(--gray-200) !important;
}

.back-button:active {
  color: var(--gray-600) !important;
}

.pi-arrow-left {
  color: var(--gray-600) !important;
  font-size: 12px !important;
}
</style>
